/**
 * Bulk Patch — mass Change-Order updates across many orgs.
 *
 * Parent screen with a mode toggle:
 *   - **Edit in UI** (default): pick an org, the UI fetches its deals
 *     + children + products and renders editable rows inline.
 *   - **XLSX import** (advanced): the original spreadsheet-based path,
 *     kept for batch workflows where editing in a browser table would
 *     be slower than editing locally.
 *
 * Both modes post to the same backend (`POST /aws/deals/bulk-patch`)
 * with the same row payload, so the dry-run / apply semantics are
 * identical regardless of which path produced the rows.
 *
 * Renders inside a plain scroll `Box` that fills `AppLayout`'s MainContent
 * (height:100vh / overflow:hidden) with `height: 100%` and scrolls
 * internally — a fixed-height `PageContainer` was always shorter than that
 * 100vh parent and left a dead band at the bottom. Top padding clears the
 * floating nav bar when it's pinned.
 */

import {
  Box,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider';
import React, { useState } from 'react';

import UiDrivenBulkPatchFlow from './UiDrivenBulkPatchFlow';
import XlsxImportFlow from './XlsxImportFlow';

type Mode = 'ui' | 'xlsx';

/** Clears the floating nav bar when it's pinned. */
const NAV_PINNED_OFFSET_PX = 55;

const BulkPatchImportPage: React.FC = () => {
  const theme = useTheme();
  const layout = useLayoutContext();
  const isPinned = layout?.pinned ?? false;
  const [mode, setMode] = useState<Mode>('ui');

  return (
    // Plain scroll Box instead of PageContainer. MainContent is height:100vh /
    // overflow:hidden, so a fixed-height container (calc(100vh - Npx)) was
    // always shorter than its parent and left a dead band at the bottom.
    // height:100% fills MainContent exactly and scrolls internally.
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        bgcolor: theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1400,
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 4 },
          pt: {
            xs: isPinned ? `${NAV_PINNED_OFFSET_PX + 16}px` : 2,
            md: isPinned ? `${NAV_PINNED_OFFSET_PX + 24}px` : 3,
          },
          pb: { xs: 6, md: 8 },
        }}
      >
        {/* ── Header ─────────────────────────────────────────────── */}
        <Stack spacing={1} sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}
          >
            Bulk Patch
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 820 }}
          >
            Mass-update deal budgets, margins, and dates. Each change creates a
            Change Order patch (semver bump) on the target deal. Finalized
            contracts and terminal deals are blocked; the preview shows exactly
            what will change before you commit.
          </Typography>
        </Stack>

        {/* ── Mode tabs ──────────────────────────────────────────── */}
        <Paper
          variant="outlined"
          sx={{
            mb: 4,
            borderRadius: 2,
            overflow: 'hidden',
            borderColor: 'divider',
          }}
        >
          <Tabs
            value={mode}
            onChange={(_, v: Mode) => setMode(v)}
            aria-label="Bulk patch mode"
            variant="fullWidth"
            sx={{
              minHeight: 56,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.95rem',
                minHeight: 56,
              },
            }}
          >
            <Tab value="ui" label="Edit in UI" />
            <Tab value="xlsx" label="XLSX import (advanced)" />
          </Tabs>
        </Paper>

        {/* ── Active flow ────────────────────────────────────────── */}
        <Box sx={{ pb: 6 }}>
          {mode === 'ui' ? <UiDrivenBulkPatchFlow /> : <XlsxImportFlow />}
        </Box>
      </Box>
    </Box>
  );
};

export default BulkPatchImportPage;
